import { useState, useEffect, useMemo } from 'react';
import {
  isExceptionExist,
  type Cart,
  ErrorResponse,
  ExceptionsEnum,
  getActiveCart,
  AccountProcessType,
  isGroupOrder,
  userNotRegistered,
  isPVBVEarnedAndUpliftPresent,
  SubCartDetail,
} from '@commerce-cart-webcomponents/sdk';
import { formatPrice, formatNumber } from '@nextgen-web-framework/core';
import {
  useTranslation,
  ButtonSize,
  ButtonStyles,
  Button,
  FAIcon,
  Theme,
  useSession,
  useNextgenRouter,
} from '@nextgen-web-framework/all';
import { AccountProductType } from '@commerce-cart-webcomponents/sdk/services/models/AccountProductType';
import { CartOverviewItem } from '../cart-overview-item/CartOverviewItem';
import { useFeatureFlag } from '../../../common';
import { FulfillmentExceptionMessageKey } from './types/enums';
import styles from './CartOverview.module.scss';

export interface CartOverviewProps {
  data: SubCartDetail;
  cartData: Cart;
  clickCheckoutHandler: () => void;
  isDesktop?: boolean;
  showChild: (val: boolean) => void;
  checkoutBtnDisable?: boolean;
  displayGCO?: boolean;
  displayCheckoutBtn: boolean;
  displayPVBV: boolean;
}

export const CartOverview = ({
  data,
  cartData,
  clickCheckoutHandler,
  isDesktop,
  checkoutBtnDisable = false,
  showChild,
  displayPVBV,
  displayCheckoutBtn,
  displayGCO,
}: CartOverviewProps) => {
  const [shippingFree, setShippingFree] = useState<boolean>(false);
  const [registrationFeeTitle, setRegistrationFeeTitle] = useState<string>('');
  const notRegistered = userNotRegistered(cartData);
  const isRenewal = cartData?.accountProcess?.type === AccountProcessType.Renewal;
  const { isABO, isMember } = useSession();
  const { hideBVFlag, cartSelectionFlag } = useFeatureFlag();
  const t = useTranslation();
  const router = useNextgenRouter();
  const feeProduct = useMemo(() => {
    const cartId = router.params?.cartId ?? '';
    const activeCart = getActiveCart(cartData, cartId as string);
    return activeCart?.entries?.find((entry) => entry?.accountProductTypes?.includes(AccountProductType.Fee));
  }, [cartData]);

  const showCoupons = () =>
    (isGroupOrder(cartData.orderType) && !displayCheckoutBtn) || !isGroupOrder(cartData.orderType);

  useEffect(() => {
    setShippingFree((cartData.shippingFee?.freeShipping as boolean) || Boolean(cartData?.isSopFreeDelivery));
  }, [cartData]);

  const selectedCartCount = cartData.subCarts.reduce((acc, { selected }) => {
    if (selected) {
      return acc + 1;
    }
    return acc;
  }, 0);

  const subtotalText = t('cart.cartOverview.subtotal');
  const freeText = t('cart.cartOverview.free');
  const totalText = t('cart.cartOverview.total');
  const renewalFee = t('cart.aboMembershipRenewal.renewalFee');
  const registrationFee = t('cart.aboMembershipRenewal.registrationFee');
  const noCartSelectedText = 'No carts selected';
  const showNoCartSelectedCondition =
    (cartSelectionFlag && isGroupOrder(cartData.orderType) && selectedCartCount > 0) ||
    !displayGCO ||
    !cartSelectionFlag;

  const showPVBV = isABO() && displayPVBV;
  const showPVBVUplift = () => {
    if (!isGroupOrder(cartData.orderType)) {
      return showPVBV;
    }

    return !displayCheckoutBtn;
  };

  const checkoutButtonTextWithCount =
    cartSelectionFlag && isGroupOrder(cartData.orderType)
      ? t('cart.cartTotal.selectedCartCheckout', {
          selectedCartCount: String(selectedCartCount),
        })
      : t('cart.cartTotal.checkout');

  useEffect(() => {
    setRegistrationFeeTitle(isRenewal ? renewalFee : registrationFee);
  }, [isABO, isMember]);

  const [
    {
      totalEarnedAmwayValue,
      totalUpliftAmwayValue,
      // totalAmPointDiscount = 0,
      // totalEarnedAmPoint = 0,
      // totalAmPoint = 0,
    },
  ] = cartData.subCarts;

  const { pointValue: earnedPV, businessVolume: earnedBV } = data.totalEarnedAmwayValue || totalEarnedAmwayValue || {};
  const { pointValue: upliftPV, businessVolume: upliftBV } = data.totalUpliftAmwayValue || totalUpliftAmwayValue || {};

  //const ampointsUpliftPresent = isMember() && Boolean(totalAmPointDiscount);
  const pvbvEarnedAndUpliftPresent = isPVBVEarnedAndUpliftPresent(upliftPV, upliftBV, earnedPV, earnedBV);

  return (
    <div className={styles['price_summary_container']}>
      <div
        className={styles['price_summary_container_overview']}
        data-testid="overview-test-id"
        role="presentation"
        onClick={() => showChild(false)}
        onKeyPress={() => showChild(false)}
      >
        {t('cart.cartOverview.OrderSummary')}
        {!isDesktop && <FAIcon iconName="chevron-down" className={styles['chevron_icon']} />}
      </div>

      {/* Subtotal */}
      <div className={styles['price_summary_container_wrapper']}>
        {showNoCartSelectedCondition ? (
          <div className={styles['price_summary_container_wrapper_section_1']}>
            <div className={styles['price_summary_container_subtotal']} data-testid="subtotal-test-id">
              <div className={styles['price_summary_container_subtotal_text']}>{subtotalText}</div>
              <div className={styles['price_summary_container_subtotal_amt']}>
                {isGroupOrder(cartData.orderType) && displayCheckoutBtn
                  ? formatPrice(Number(cartData.subTotal?.value ?? 0))
                  : formatPrice(Number(data.totalPriceWithoutDiscount?.value ?? 0))}
              </div>
            </div>
            {feeProduct && (
              // Fee Row
              <div className={styles['price_summary_container_subtotal']} data-testid="fee-test-id">
                <div className={styles['price_summary_container_subtotal_text']}>{registrationFeeTitle}</div>
                <div className={styles['price_summary_container_subtotal_amt']}>
                  {formatPrice(Number(feeProduct.totalPrice?.value ?? 0))}
                </div>
              </div>
            )}
            {/*Promotions */}
            {Boolean(data?.totalPromotionDiscount?.value) && (
              <div
                // data-testid="promotion-click"
                data-testid="promotion-test-id"
                className={styles['price_summary_container_promotion']}
              >
                <div className={styles['price_summary_container_promotion_text']}>
                  {t('cart.cartOverview.promotions')}
                </div>
                <div className={`${styles['price_summary_container_promotion_amt']} ${styles['display-promotion']}`}>
                  <span>-{formatPrice(Number(data?.totalPromotionDiscount?.value))}</span>
                </div>
              </div>
            )}

            {/*Coupons */}
            {showCoupons() && Boolean(data?.totalCouponDiscount?.value) && (
              <div data-testid="coupon-test-id" className={styles['price_summary_container_promotion']}>
                <div className={styles['price_summary_container_promotion_text']}>{t('cart.cartOverview.coupons')}</div>
                <div className={`${styles['price_summary_container_promotion_amt']} ${styles['display-promotion']}`}>
                  <span>-{formatPrice(Number(data?.totalCouponDiscount?.value ?? 0))}</span>
                </div>
              </div>
            )}

            {/* Shipping */}
            {displayCheckoutBtn && (
              <div className={styles['price_summary_container_shipping']} data-testid="shipping-test-id">
                <div className={styles['price_summary_container_shipping_text']}>{t('cart.cartOverview.shipping')}</div>
                <div className={styles['price_summary_container_total_amt']}>
                  {!shippingFree && (
                    <span>
                      {isExceptionExist(
                        ExceptionsEnum.Exceptions.FulfillmentService,
                        (cartData.errors ?? []) as ErrorResponse[],
                      ) ? (
                        <div data-testid="shipping-exception-text" className={styles['shipping-missing-text']}>
                          {t(FulfillmentExceptionMessageKey[ExceptionsEnum.Exceptions.FulfillmentService])}
                        </div>
                      ) : (
                        formatPrice(Number(cartData.shippingFee?.deliveryFee ?? 0))
                      )}
                    </span>
                  )}
                  {shippingFree && (
                    <span className={styles['price_summary_container_total_amt_free']} data-testid="free-text">
                      {freeText}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          <span className={styles['no-cart-selected-text']}>{noCartSelectedText}</span>
        )}

        {/* Total */}
        <div className={styles['price_summary_container_border']}></div>
        {showNoCartSelectedCondition && (
          <div className={styles['price_summary_container_wrapper_section_2']}>
            <CartOverviewItem
              itemName={totalText}
              itemValue={formatPrice(Number(data?.totalPrice?.value || '0.00'))}
              containerClass="price_summary_container_total"
              itemNameClass="price_summary_container_total_text"
              itemValueClass="price_summary_container_total_amt"
            />
            {/*PV BV Earned */}
            {showPVBVUplift() && Boolean(pvbvEarnedAndUpliftPresent) && (
              <CartOverviewItem
                itemName={hideBVFlag ? t('cart.cartTotal.pvEarned') : t('cart.cartTotal.pvbvEarned')}
                itemValue={`${formatNumber(earnedPV as number)} ${
                  hideBVFlag ? '' : `/ ${formatNumber(earnedBV as number)}`
                }`}
                containerClass="price_summary_container_pvbvUplift"
                itemNameClass="price_summary_container_pvbvUplift_text"
                itemValueClass="price_summary_container_pvbvUplift_text"
              />
            )}
            {/*PV BV Uplift */}
            {showPVBVUplift() && Boolean(pvbvEarnedAndUpliftPresent) && (
              <CartOverviewItem
                itemName={hideBVFlag ? t('cart.cartTotal.pvUplift') : t('cart.cartTotal.pvbvUplift')}
                itemValue={`${formatNumber(upliftPV as number)} ${
                  hideBVFlag ? '' : `/ ${formatNumber(upliftBV as number)}`
                }`}
                containerClass="price_summary_container_pvbvUplift"
                itemNameClass="price_summary_container_pvbvUplift_text"
                itemValueClass="price_summary_container_pvbvUplift_values"
              />
            )}
            {/*Points */}
            {showPVBV && Boolean(data.totalPrice?.amwayValue?.pointValue) && (
              <CartOverviewItem
                itemName={hideBVFlag ? t('cart.cartTotal.pvMobile') : t('cart.cartTotal.pvbvMobile')}
                itemValue={`${formatNumber(data.totalPrice?.amwayValue?.pointValue as number)} ${
                  hideBVFlag ? '' : `/ ${formatNumber(data.totalPrice?.amwayValue?.businessVolume as number)}`
                }`}
                containerClass="price_summary_container_points"
                itemNameClass="price_summary_container_points_text"
                itemValueClass="price_summary_container_points_value"
              />
            )}
            {/*AmPoints */}
            {/* {Boolean(totalEarnedAmPoint) && ampointsUpliftPresent && (
            <CartOverviewItem
              itemName={t('cart.cartOverview.APointsEarned')}
              itemValue={formatNumber(totalEarnedAmPoint)}
              containerClass="price_summary_container_pvbvUplift"
              itemNameClass="price_summary_container_pvbvUplift_text"
              itemValueClass="price_summary_container_pvbvUplift_text"
            />
          )}
          {ampointsUpliftPresent && (
            <CartOverviewItem
              itemName={t('cart.cartOverview.APointsUplift')}
              itemValue={formatNumber(totalAmPointDiscount)}
              containerClass="price_summary_container_pvbvUplift"
              itemNameClass="price_summary_container_pvbvUplift_text"
              itemValueClass="price_summary_container_pvbvUplift_values"
            />
          )}
          {isMember() && Boolean(totalAmPoint) && (
            <CartOverviewItem
              itemName={t('cart.cartOverview.TotalAPoints')}
              itemValue={formatNumber(totalAmPoint)}
              containerClass="price_summary_container_points"
              itemNameClass="price_summary_container_points_text"
              itemValueClass="price_summary_container_points_text"
            />
          )}
          {ampointsUpliftPresent && (
            <span className={styles['price_summary_container_ApointsCalcDec']}>{t('cart.cartTotal.ACalcDesc')}</span>
          )} */}
          </div>
        )}
      </div>

      {/* Checkout button desktop */}
      {displayCheckoutBtn && (
        <div className={styles['cart-total-checkout-btn-desktop']} data-testid="checkout-test-id">
          <Button
            id="proceed-to-checkout-btn-overview"
            onClick={clickCheckoutHandler}
            title={notRegistered ? t('cart.cartTotal.registerAndCheckout') : checkoutButtonTextWithCount}
            tabIndex={0}
            buttonStyle={ButtonStyles.SOLID}
            theme={Theme.BLACK}
            size={ButtonSize.MEDIUM}
            disabled={checkoutBtnDisable}
            data-testid="proceed-checkout-btn-overview-desktop"
            aria-label="proceed-checkout-btn"
          />
        </div>
      )}
    </div>
  );
};
